'use client'

import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { testIds } from '@/root/shared-consts'
import { useFormContext, Controller } from 'react-hook-form'
import { useCustomRouter, useGetTranslations } from '@/root/shared-hooks'
import { useAppConfig } from '@/root/shared-hooks/appConfig/useAppConfig'
import {
  CancelButton,
  CitizenshipSelect,
  ControlledInput,
  ControlledTextarea,
  GenderToggle,
  FormField,
  Separator,
  BirthDateSelect,
  SaveButton,
  DeleteTicketButton,
} from '@/root/modules/shared/SelectTicketPage/components/PassengerForm'
import { Gender } from '@/root/shared-providers/BookingCart/types'
import { getCountryOptions } from '@/root/modules/shared/SelectTicketPage/helpers/countries/countries'
import { CountryItem } from '@/root/shared-types'
import { FormValues, PassengerFormData } from '@/root/shared-types/selectTicketPage/selectTicket'
import { createDefaultPassenger } from '@/root/modules/shared/SelectTicketPage/utils/passenger'
import type { ResidentOverrideCategory } from '@/root/modules/shared/SelectTicketPage/helpers/residentOverride/residentOverride'
import { Button } from '@/root/ui/shared/Button/Button'
import { Spinner } from '@/root/ui/shared/Spinner/Spinner'
import { useResidentPriceCategory } from '@/root/shared-hooks/resident/useResidentPriceCategory'
import { DateTime } from 'luxon'
import cn from 'classnames'
import { faker } from '@faker-js/faker'
import { getRuntimeConfig } from '@/root/utils/runtimeConfig'

export interface ResidentOverrideOption extends ResidentOverrideCategory {
  error?: string
  onToggle: (checked: boolean) => Promise<void>
}

interface PassengerFormProps {
  countries: CountryItem[]
  index: number
  isResident: boolean
  onDelete: () => void | Promise<void>
  onSave: (data: PassengerFormData) => void | Promise<void>
  departureDate?: string
  disabled?: boolean
  onClose?: () => void
  onResidentIdVerified?: (personalId: string, priceCategory: string) => Promise<boolean>
  passenger?: PassengerFormData
  personalIdError?: string
  personalIdOnly?: boolean
  priceCategoryTitle?: string
  residentOverride?: ResidentOverrideOption
  route?: string
}

export const PassengerForm: FC<PassengerFormProps> = ({
  onSave,
  onDelete,
  countries,
  index,
  passenger,
  route,
  departureDate,
  disabled,
  onClose,
  isResident,
  onResidentIdVerified,
  personalIdError,
  personalIdOnly = false,
  priceCategoryTitle,
  residentOverride,
}) => {
  const { control, resetField, getValues, watch, setValue } = useFormContext<FormValues>()
  const { locale } = useCustomRouter()
  const { preferredCitizenships } = useAppConfig()
  const countryOptions = useMemo(
    () => getCountryOptions(locale, countries, preferredCitizenships),
    [locale, countries, preferredCitizenships]
  )
  const countryCodes = useMemo(
    () => countryOptions.filter((opt) => opt.type === 'country').map((opt) => opt.value),
    [countryOptions]
  )
  const { getTranslation } = useGetTranslations()
  const isDev = getRuntimeConfig().ENABLE_FAKE_FILL === 'true'

  const firstName = watch(`passengers.${index}.firstName`)
  const lastName = watch(`passengers.${index}.lastName`)
  const citizenship = watch(`passengers.${index}.citizenship`)
  const gender = watch(`passengers.${index}.gender`)
  const birthdate = watch(`passengers.${index}.birthdate`)
  const personalIdConfirmed = watch(`passengers.${index}.personalIdConfirmed`)

  const isBirthdateFilled = birthdate ? DateTime.fromISO(birthdate).isValid : false

  const fieldsToCheck = [firstName, lastName, citizenship, gender]
  const isSaveDisabled = fieldsToCheck.some((f) => !f?.trim()) || !isBirthdateFilled

  const personalId = watch(`passengers.${index}.personalId`) ?? ''
  const isPersonalIdValid = /^\d{11}$/.test(personalId)

  const priceCategory = watch(`passengers.${index}.priceCategory`)
  const isOverrideChecked = !!residentOverride && priceCategory === residentOverride.code
  const [overridePending, setOverridePending] = useState(false)
  const overrideId = `resident-override-${index}`

  const savedFirstName = passenger?.firstName
  const savedLastName = passenger?.lastName
  const isName = savedFirstName && savedLastName
  const showName = isName ? `${savedFirstName} ${savedLastName}` : getTranslation('selectTicketsPassengerStatus')
  const isSavedPassenger = Boolean(passenger)

  const isFormLocked = (isResident && !personalIdConfirmed) || !!disabled

  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const {
    data: residentData,
    error: residentError,
    loading: residentLoading,
    fetchResidentPriceCategory,
    resetError,
  } = useResidentPriceCategory()

  const onResidentIdVerifiedRef = useRef(onResidentIdVerified)
  onResidentIdVerifiedRef.current = onResidentIdVerified

  useEffect(() => {
    if (!residentData) return
    const pid = getValues(`passengers.${index}.personalId`) ?? ''
    setValue(`passengers.${index}.priceCategory`, residentData.priceCategory, { shouldDirty: true })
    const promise = onResidentIdVerifiedRef.current?.(pid, residentData.priceCategory)
    if (promise) {
      void promise
        .then((wasApplied) => {
          if (wasApplied) setValue(`passengers.${index}.personalIdConfirmed`, true, { shouldDirty: true })
        })
        .catch(() => undefined)
    } else {
      setValue(`passengers.${index}.personalIdConfirmed`, true, { shouldDirty: true })
    }
  }, [residentData, index, setValue, getValues])

  useEffect(() => {
    if (personalIdError) {
      setValue(`passengers.${index}.personalIdConfirmed`, false)
    }
  }, [personalIdError, index, setValue])

  const fillRandomPassengerData = () => {
    if (isFormLocked) return

    const birthDate = faker.date.birthdate({ min: 16, max: 67, mode: 'age' })
    const randomGender: Gender = faker.helpers.arrayElement(['M', 'F'])
    const randomCitizenship = countryCodes.length > 0 ? faker.helpers.arrayElement(countryCodes) : ''

    setValue(`passengers.${index}.firstName`, faker.person.firstName(), { shouldDirty: true })
    setValue(`passengers.${index}.lastName`, faker.person.lastName(), { shouldDirty: true })
    setValue(`passengers.${index}.birthdate`, DateTime.fromJSDate(birthDate).toISODate() ?? '', { shouldDirty: true })
    setValue(`passengers.${index}.citizenship`, randomCitizenship, { shouldDirty: true })
    setValue(`passengers.${index}.gender`, randomGender, { shouldDirty: true })
    setValue(`passengers.${index}.additionalInfo`, faker.lorem.words(3), { shouldDirty: true })
  }

  const residentIdErrorText =
    personalIdError ??
    (residentError?.code ? getTranslation(residentError.code as Parameters<typeof getTranslation>[0]) : undefined)

  return (
    <div className="flex flex-col">
      {isResident && (
        <section
          className={cn(
            'flex flex-col',
            personalIdOnly && personalIdConfirmed ? 'gap-1' : 'gap-5',
            personalIdOnly ? 'pt-0' : 'pt-2.5'
          )}
        >
          <h2 className="sr-only">{getTranslation('selectTicketsEnterPersonalId')}</h2>
          <div className="flex w-full items-end gap-4">
            {!personalIdConfirmed ? (
              <>
                <div className="flex-1">
                  <FormField label={getTranslation('selectTicketsEnterPersonalId')}>
                    <div className="relative">
                      <ControlledInput
                        name={`passengers.${index}.personalId`}
                        control={control}
                        className="pe-9"
                        data-testid={testIds.tickets.inputLocalId}
                        placeholder={getTranslation('selectTicketsResidentId')}
                        autoFocus
                        inputMode="numeric"
                        maxLength={11}
                        disabled={disabled}
                        aria-busy={residentLoading}
                        error={!!residentIdErrorText}
                        rules={{
                          required: true,
                          pattern: /^\d{11}$/,
                        }}
                      />
                      {residentLoading && (
                        <span className="pointer-events-none absolute inset-y-0 inset-e-2 flex items-center">
                          <Spinner className="border-neutral-lightest border-t-frozen-ice size-5 border-2" />
                        </span>
                      )}
                    </div>
                  </FormField>
                </div>

                <Button
                  type="button"
                  variant="default"
                  className="bg-interactive darken flex h-9 w-23 items-center justify-center rounded-lg text-base text-white"
                  data-testid={testIds.tickets.checkLocalId}
                  label={getTranslation('buttonCheckId')}
                  disabled={!isPersonalIdValid || residentLoading || disabled}
                  onClick={() => {
                    if (!isPersonalIdValid || !route || !departureDate) return

                    fetchResidentPriceCategory({
                      route,
                      residentId: personalId,
                      departureDate,
                    })
                  }}
                />
              </>
            ) : personalIdOnly ? (
              <div className="flex flex-col gap-1 text-sm">
                {priceCategoryTitle && <p className="text-neutral-darker text-base font-bold">{priceCategoryTitle}</p>}
                <div className="flex items-center gap-2">
                  <p className="text-neutral-darker">{getTranslation('selectTicketsPersonalId')}:</p>
                  <span className="text-success-darker font-bold">{personalId}</span>
                </div>
              </div>
            ) : (
              <FormField label={getTranslation('selectTicketsPersonalId')}>
                <div className="text-success-dark flex h-9 items-center font-bold">{personalId}</div>
              </FormField>
            )}
          </div>

          {(!personalIdConfirmed || !personalIdOnly) && (
            <p
              className={cn('text-error -mt-4 min-h-5 text-sm', !personalIdOnly && '-mb-4')}
              role="alert"
              aria-live="polite"
            >
              {!personalIdConfirmed && residentIdErrorText}
            </p>
          )}

          {residentOverride && personalIdConfirmed && (
            <div>
              <label
                htmlFor={overrideId}
                className={cn(
                  'flex items-start gap-3 py-1 text-sm',
                  disabled && 'cursor-not-allowed opacity-60',
                  !disabled && !overridePending && 'cursor-pointer'
                )}
              >
                <span className="relative mt-0.5 inline-flex size-4.5 shrink-0 items-center justify-center">
                  <input
                    id={overrideId}
                    type="checkbox"
                    data-testid={testIds.tickets.residentOverrideCheckbox}
                    checked={isOverrideChecked}
                    disabled={disabled || overridePending}
                    aria-busy={overridePending}
                    aria-labelledby={`${overrideId}-label`}
                    aria-describedby={`${overrideId}-status`}
                    className={cn(
                      'accent-interactive size-4.5 shrink-0 disabled:cursor-not-allowed',
                      overridePending && 'opacity-0'
                    )}
                    onChange={(event) => {
                      const { checked } = event.target
                      setOverridePending(true)
                      void residentOverride
                        .onToggle(checked)
                        .catch(() => undefined)
                        .finally(() => setOverridePending(false))
                    }}
                  />
                  {overridePending && (
                    <Spinner className="border-interactive/30 border-t-interactive absolute size-4.5 border-2" />
                  )}
                </span>
                <span id={`${overrideId}-label`} className="text-neutral-darker leading-snug">
                  {residentOverride.label}
                </span>
              </label>
              <output
                id={`${overrideId}-status`}
                data-testid={testIds.tickets.residentOverrideError}
                className="text-error mt-1 block text-sm leading-snug empty:hidden"
              >
                {residentOverride.error ?? ''}
              </output>
            </div>
          )}

          {!personalIdOnly && (
            <>
              <Separator />
              <div className="flex gap-2 text-sm">
                <p className="text-neutral-darker">{getTranslation('selectTicketsPassengerLabel')}</p>
                <span className={cn(isName ? 'text-success-darker font-bold' : 'text-error')}>{showName}</span>
              </div>
            </>
          )}
        </section>
      )}

      {!personalIdOnly && (
        <section className="flex flex-col gap-5 pt-2.5">
          <div className="grid grid-cols-2 gap-4">
            <FormField label={getTranslation('selectTicketsFirstname')} requiredField>
              <ControlledInput
                name={`passengers.${index}.firstName`}
                control={control}
                data-testid={testIds.tickets.passengerFirstName}
                placeholder={getTranslation('selectTicketsRequiredField')}
                autoFocus
                disabled={isFormLocked}
              />
            </FormField>

            <FormField label={getTranslation('selectTicketsLastname')} requiredField>
              <ControlledInput
                name={`passengers.${index}.lastName`}
                control={control}
                data-testid={testIds.tickets.passengerLastName}
                placeholder={getTranslation('selectTicketsRequiredField')}
                disabled={isFormLocked}
              />
            </FormField>
          </div>

          <FormField label={getTranslation('selectTicketsBirthdate')} requiredField>
            <Controller
              name={`passengers.${index}.birthdate`}
              control={control}
              render={({ field }) => (
                <BirthDateSelect birthdate={field.value ?? ''} onChange={field.onChange} disabled={isFormLocked} />
              )}
            />
          </FormField>

          <div className="flex w-full justify-between">
            <FormField label={getTranslation('selectTicketsCitizenship')} requiredField>
              <Controller
                name={`passengers.${index}.citizenship`}
                control={control}
                render={({ field }) => (
                  <span data-testid={testIds.tickets.passengerCitizenship}>
                    <CitizenshipSelect
                      value={field.value ?? ''}
                      options={countryOptions}
                      onChange={field.onChange}
                      searchPlaceholder={getTranslation('citizenshipPlaceholder')}
                      placeholder={getTranslation('selectTicketsRequiredField')}
                      disabled={isFormLocked}
                    />
                  </span>
                )}
              />
            </FormField>

            <FormField label={getTranslation('selectTicketsGender')} requiredField>
              <Controller
                name={`passengers.${index}.gender`}
                control={control}
                render={({ field }) => (
                  <span data-testid={testIds.tickets.passengerGender}>
                    <GenderToggle value={field.value as Gender} onChange={field.onChange} disabled={isFormLocked} />
                  </span>
                )}
              />
            </FormField>
          </div>

          <FormField label={getTranslation('selectTicketsExtraInfo')}>
            <ControlledTextarea
              name={`passengers.${index}.additionalInfo`}
              control={control}
              disabled={isFormLocked}
              data-testid={testIds.tickets.passengerExtraInfo}
            />
          </FormField>

          {isDev && !isSavedPassenger && (
            <Button
              type="button"
              variant="default"
              className="border-warning-dark text-neutral-darkest h-8 w-28 self-start border-2 bg-[repeating-linear-gradient(135deg,#fff4b0_0px,#fff4b0_10px,#d7f7b8_10px,#d7f7b8_20px)] px-2 text-xs font-bold shadow-sm"
              onClick={fillRandomPassengerData}
              disabled={isFormLocked}
            >
              <span className="flex h-full items-center justify-center leading-none">Fill random</span>
            </Button>
          )}

          <div className="flex w-full justify-between gap-5">
            <CancelButton
              title={getTranslation('buttonCancel')}
              data-testid={testIds.tickets.passengerCancel}
              disabled={disabled}
              onClick={() => {
                resetField(`passengers.${index}`, {
                  defaultValue: passenger ?? createDefaultPassenger(),
                })
                resetError?.()
              }}
            />
            <SaveButton
              title={getTranslation('buttonSave')}
              data-testid={testIds.tickets.passengerSave}
              disabled={isSaveDisabled || isFormLocked}
              loading={saving}
              className="pt-0 pb-0"
              onClick={async () => {
                setSaving(true)
                try {
                  await onSave(getValues(`passengers.${index}`))
                  onClose?.()
                } finally {
                  setSaving(false)
                }
              }}
            />
          </div>

          <Separator />
          <DeleteTicketButton
            title={getTranslation('buttonDeleteTicket')}
            data-testid={testIds.tickets.deleteTicketButton}
            loading={deleting}
            disabled={disabled}
            onClick={async () => {
              setDeleting(true)
              try {
                await onDelete()
              } finally {
                setDeleting(false)
              }
            }}
          />
        </section>
      )}
    </div>
  )
}
